import { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, Plus, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { TagBadge } from './TagBadge';
import { fetchTags, assignTagToCandidate, removeTagFromCandidate } from '@/lib/api';
import type { CandidateTag } from '@/types';

interface TagSelectorProps {
  candidateId: string;
  assignedTags: CandidateTag[];
  className?: string;
}

export function TagSelector({ candidateId, assignedTags, className }: TagSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();

  // Fetch all available tags
  const { data: allTags = [], isLoading: isLoadingTags } = useQuery({
    queryKey: ['tags'],
    queryFn: fetchTags,
  });

  // Filter out already assigned tags
  const assignedTagIds = new Set(assignedTags.map((ct) => ct.tagId));
  const availableTags = allTags.filter((tag) => !assignedTagIds.has(tag.id));

  // Assign tag mutation
  const assignMutation = useMutation({
    mutationFn: (tagId: string) => assignTagToCandidate(candidateId, tagId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['candidate', candidateId] });
      queryClient.invalidateQueries({ queryKey: ['candidateTags', candidateId] });
    },
  });

  // Remove tag mutation
  const removeMutation = useMutation({
    mutationFn: (tagId: string) => removeTagFromCandidate(candidateId, tagId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['candidate', candidateId] });
      queryClient.invalidateQueries({ queryKey: ['candidateTags', candidateId] });
    },
  });

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleAssignTag = (tagId: string) => {
    assignMutation.mutate(tagId);
    setIsOpen(false);
  };

  const handleRemoveTag = (tagId: string) => {
    removeMutation.mutate(tagId);
  };

  const isLoading = assignMutation.isPending || removeMutation.isPending;

  return (
    <div className={cn('space-y-3', className)}>
      {/* Assigned Tags */}
      <div className="flex flex-wrap gap-2">
        {assignedTags.length > 0 ? (
          assignedTags.map((candidateTag) => (
            <TagBadge
              key={candidateTag.tagId}
              name={candidateTag.tag.name}
              color={candidateTag.tag.color}
              onRemove={
                removeMutation.isPending
                  ? undefined
                  : () => handleRemoveTag(candidateTag.tagId)
              }
            />
          ))
        ) : (
          <span className="text-sm text-text-tertiary">No tags assigned</span>
        )}
      </div>

      {/* Add Tag Dropdown */}
      <div className="relative" ref={dropdownRef}>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setIsOpen(!isOpen)}
          disabled={isLoading || availableTags.length === 0}
          className="gap-1.5"
        >
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Plus className="h-4 w-4" />
          )}
          Add Tag
          <ChevronDown
            className={cn('h-4 w-4 transition-transform', isOpen && 'rotate-180')}
          />
        </Button>

        {isOpen && (
          <div className="absolute z-50 mt-1 w-56 rounded-md border bg-bg-tertiary shadow-lg">
            <div className="max-h-60 overflow-auto py-1">
              {isLoadingTags ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="h-5 w-5 animate-spin text-text-tertiary" />
                </div>
              ) : availableTags.length > 0 ? (
                availableTags.map((tag) => (
                  <button
                    key={tag.id}
                    type="button"
                    onClick={() => handleAssignTag(tag.id)}
                    className="flex w-full items-center gap-2 px-3 py-2 text-sm hover:bg-white/10"
                  >
                    <span
                      className="h-3 w-3 rounded-full flex-shrink-0"
                      style={{ backgroundColor: tag.color }}
                    />
                    <span className="truncate">{tag.name}</span>
                  </button>
                ))
              ) : (
                <div className="px-3 py-4 text-center text-sm text-text-tertiary">
                  All tags have been assigned
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
